import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "../../components/Layout/Layout";
import UserLayout from "../../components/Layout/UserLayout";
import { useAuth } from "../../context/AuthContext";
import LeadActivityTimeline from "./LeadActivityTimeline";
import VisitModal from "../Visits/VisitModal";
import {
  QuickStageModal,
  QuickStatusModal,
  QuickNoteModal,
  QuickFollowUpModal,
  QuickSendEmailModal,
} from "./QuickActionModals";
import {
  Card,
  Button,
  Badge,
  Input,
  Select,
  Modal,
  Spinner,
  ImagePreviewModal,
  isImageAttachment,
} from "../../components/UI";
import api, { leadsAPI, callLogsAPI, usersAPI, pipelineAPI, followUpsAPI, emailTemplatesAPI, filePublicUrl } from "../../services/api";
import toast from "react-hot-toast";
import {
  HiOutlineArrowLeft,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlinePhone,
  HiOutlineMail,
  HiOutlineSave,
  HiOutlineUserAdd,
  HiOutlineOfficeBuilding,
  HiOutlineLocationMarker,
  HiOutlineGlobeAlt,
  HiOutlineIdentification,
  HiOutlineCurrencyDollar,
  HiOutlineTag,
  HiOutlineUser,
  HiOutlineUsers,
  HiOutlinePlay,
  HiOutlineDownload,
  HiOutlinePaperClip,
  HiOutlineLightningBolt,
  HiOutlineClock,
  HiOutlineStar,
  HiOutlineClipboardCheck,
  HiOutlineChatAlt,
  HiOutlineCalendar,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineExclamationCircle,
} from "react-icons/hi";
import { format } from "date-fns";
import { sanitizePhoneInput, validatePhoneNumber, validateOptionalPhone, sanitizePincodeInput, validatePincode, sanitizeGstInput, validateGstNumber, validateEmail } from "../../utils/validation";
import { formatLabel } from '../../utils/formatLabel';

const statusOptions = [
  { value: "hot", label: "Hot" },
  { value: "warm", label: "Warm" },
  { value: "cold", label: "Cold" },
];

// Parses the lead's combined notes string into individual note entries.
// Quick-action notes are appended by the backend as "[ISO timestamp] text"
// lines; lines without a timestamp are either a continuation of the previous
// note (multi-line note text) or a plain imported note.
const parseNotes = (notes) => {
  if (!notes) return [];
  const entries = [];
  notes.split("\n").forEach((line) => {
    if (!line.trim()) return;
    const match = line.match(
      /^\[(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z)\]\s?([\s\S]*)$/
    );
    if (match) {
      entries.push({ at: match[1], text: match[2] });
    } else if (entries.length > 0) {
      // Continuation of the previous note's text
      entries[entries.length - 1].text += `\n${line}`;
    } else {
      // No earlier entry — a plain note (e.g. imported data)
      entries.push({ at: null, text: line });
    }
  });
  return entries;
};

// Displays a labeled value row with an optional leading icon.
const InfoField = ({ label, value, icon }) => (
  <div>
    <p className="text-sm text-gray-500">{label}</p>
    <div className="flex items-center gap-2 mt-1">
      {icon && <span className="text-gray-400 flex-shrink-0">{icon}</span>}
      <span className="font-medium text-gray-900 break-words">
        {value || "-"}
      </span>
    </div>
  </div>
);

// Joins non-empty address parts into a single readable string.
const formatAddress = (address) => {
  if (!address) return "";
  const parts = [address.street, address.city, address.state, address.pincode, address.country]
    .map((p) => (p || "").trim())
    .filter(Boolean);
  return parts.join(", ");
};

// Quick actions shown above the Activity Timeline — the same set the leads
// list page offers. Email/Call tiles hide themselves when the lead has no
// email / phone (requiresEmail / requiresPhone).
const QUICK_ACTIONS = [
  {
    key: "stage",
    label: "Update Stage",
    icon: HiOutlineTag,
    tileClass: "bg-indigo-100",
    iconClass: "text-indigo-600",
  },
  {
    key: "status",
    label: "Update Status",
    icon: HiOutlineClipboardCheck,
    tileClass: "bg-purple-100",
    iconClass: "text-purple-600",
  },
  {
    key: "note",
    label: "Add Note",
    icon: HiOutlineChatAlt,
    tileClass: "bg-blue-100",
    iconClass: "text-blue-600",
  },
  {
    key: "followUp",
    label: "Schedule Follow-up",
    icon: HiOutlineCalendar,
    tileClass: "bg-amber-100",
    iconClass: "text-amber-600",
  },
  {
    key: "email",
    label: "Send Email",
    icon: HiOutlineMail,
    tileClass: "bg-emerald-100",
    iconClass: "text-emerald-600",
    requiresEmail: true,
  },
  {
    key: "call",
    label: "Call",
    icon: HiOutlinePhone,
    tileClass: "bg-green-100",
    iconClass: "text-green-600",
    requiresPhone: true,
  },
  {
    key: "visit",
    label: "Schedule Visit",
    icon: HiOutlineLocationMarker,
    tileClass: "bg-sky-100",
    iconClass: "text-sky-600",
  },
];

const LeadDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Telecallers use the User Panel layout and can't assign, reassign or call from here
  const { isTelecaller } = useAuth();
  const PageLayout = isTelecaller ? UserLayout : Layout;
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});

  // Recording playback state for the Recent Calls list.
  // recordingLog holds the call log currently being played; audioUrl holds
  // the object URL of the fetched blob (auth-protected endpoint).
  const [recordingLog, setRecordingLog] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  // Fetch the recording blob (auth-protected) whenever a call is selected to play.
  useEffect(() => {
    if (!recordingLog?.recordingPublicId) return;

    let revokeable = null;
    const fetchAudio = async () => {
      try {
        const response = await api.get(
          `/recordings/file/${recordingLog.recordingPublicId}`,
          { responseType: "blob" }
        );
        const url = URL.createObjectURL(response.data);
        revokeable = url;
        setAudioUrl(url);
      } catch (error) {
        console.error("Failed to load recording:", error);
        toast.error("Failed to load recording");
        setRecordingLog(null);
      }
    };
    fetchAudio();

    return () => {
      if (revokeable) URL.revokeObjectURL(revokeable);
    };
  }, [recordingLog?.recordingPublicId]);

  const closeRecording = () => {
    setRecordingLog(null);
    setAudioUrl(null);
  };

  // Moving to another lead (prev/next navigation) — reset transient state
  useEffect(() => {
    setRecordingLog(null);
    setAudioUrl(null);
    setIsEditing(false);
    setShowDeleteModal(false);
    setShowAssignModal(false);
    window.scrollTo({ top: 0 });
  }, [id]);

  const downloadRecording = async (log) => {
    if (!log?.recordingPublicId) return;
    try {
      const response = await api.get(
        `/recordings/file/${log.recordingPublicId}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `recording-${log._id}.mp3`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download recording");
    }
  };

  // Fetch lead details
  // Mongo ObjectIds are 24 hex chars — anything else is a broken link, don't even ask the API
  const isValidLeadId = /^[a-f0-9]{24}$/i.test(id || "");
  const { data: lead, isLoading, error: leadError, refetch: refetchLead } = useQuery({
    queryKey: ["lead", id],
    queryFn: async () => {
      const response = await leadsAPI.getById(id);
      return response.data?.data || null;
    },
    enabled: !!id && isValidLeadId,
    // 400 (bad id) / 403 / 404 won't change on retry
    retry: (failureCount, err) => {
      const status = err?.response?.status;
      if (status === 400 || status === 403 || status === 404) return false;
      return failureCount < 2;
    },
  });
  const leadErrorStatus = leadError?.response?.status;

  // ========================================
  // NEXT / PREVIOUS LEAD NAVIGATION
  // ========================================
  // Full lead list (same fetch the leads page uses for duplicate checks) so
  // prev/next can step through the whole book in the default list order.
  const { data: navLeads } = useQuery({
    queryKey: ["leads", "navList"],
    queryFn: async () => {
      const response = await leadsAPI.getAll({ limit: 10000 });
      return response.data?.data || [];
    },
    staleTime: 30000,
  });

  const navIndex = (navLeads || []).findIndex((l) => l._id === id);
  const prevLead = navIndex > 0 ? navLeads[navIndex - 1] : null;
  const nextLead =
    navIndex >= 0 && navIndex < navLeads.length - 1 ? navLeads[navIndex + 1] : null;
  const goToLead = (target) => {
    if (!target) return;
    navigate(`/app/leads/${target._id}`);
  };

  // Note: Call logs are now shown in the unified Activity Timeline component

  // Fetch pipeline stages for dropdown
  const { data: stagesData } = useQuery({
    queryKey: ["pipelineStages"],
    queryFn: async () => {
      const response = await pipelineAPI.getAll();
      return response.data?.data || [];
    },
  });

  // Create stage options for dropdown
  const stageOptions = (stagesData || []).map(stage => ({
    value: stage.key,
    label: stage.name,
    color: stage.color,
  }));

  // Create stage map for display
  const stageMap = (stagesData || []).reduce((acc, stage) => {
    acc[stage.key] = { name: stage.name, color: stage.color || '#6B7280' };
    return acc;
  }, {});

  // Fetch telecallers for assignment
  const { data: telecallers } = useQuery({
    queryKey: ["telecallers"],
    queryFn: async () => {
      const response = await usersAPI.getAll({ role: "telecaller" });
      return response.data?.data || [];
    },
    enabled: !isTelecaller,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data) => leadsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["lead", id]);
      queryClient.invalidateQueries(["leads"]);
      setIsEditing(false);
      toast.success("Lead updated successfully");
    },
    onError: () => toast.error("Failed to update lead"),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => leadsAPI.delete(id),
    onSuccess: () => {
      navigate("/app/leads");
      toast.success("Lead deleted successfully");
    },
    onError: () => toast.error("Failed to delete lead"),
  });

  // ========================================
  // QUICK ACTIONS (same set as the leads list page)
  // ========================================
  // One active action at a time: 'stage' | 'status' | 'note' | 'followUp' | 'email'
  const [activeQuickAction, setActiveQuickAction] = useState(null);
  const closeQuickAction = () => setActiveQuickAction(null);
  // Keep stage/status/note mutations working on the current lead id
  const openQuickAction = (action) => setActiveQuickAction(action);
  const [showVisitModal, setShowVisitModal] = useState(false);

  // Invalidate everything that can reflect a quick action: the lead itself,
  // the list page cache, stage tab counts and the activity timeline.
  const invalidateAfterQuickAction = () => {
    queryClient.invalidateQueries(["lead", id]);
    queryClient.invalidateQueries(["leads"]);
    queryClient.invalidateQueries(["stageStats"]);
    queryClient.invalidateQueries(["leadActivity", id]);
  };

  const quickUpdateStageMutation = useMutation({
    mutationFn: ({ id: leadId, stage }) => leadsAPI.update(leadId, { stage }),
    onSuccess: () => {
      invalidateAfterQuickAction();
      toast.success("Stage updated successfully");
      closeQuickAction();
    },
    onError: () => toast.error("Failed to update stage"),
  });

  const quickUpdateStatusMutation = useMutation({
    mutationFn: ({ id: leadId, status }) => leadsAPI.update(leadId, { status }),
    onSuccess: () => {
      invalidateAfterQuickAction();
      toast.success("Status updated successfully");
      closeQuickAction();
    },
    onError: () => toast.error("Failed to update status"),
  });

  const quickAddNoteMutation = useMutation({
    mutationFn: ({ id: leadId, note, file }) => {
      if (file) {
        const formData = new FormData();
        formData.append("note", note);
        formData.append("attachments", file);
        return leadsAPI.addNote(leadId, formData);
      }
      return leadsAPI.addNote(leadId, { note });
    },
    onSuccess: () => {
      invalidateAfterQuickAction();
      toast.success("Note added successfully");
      closeQuickAction();
    },
    onError: () => toast.error("Failed to add note"),
  });

  const quickFollowUpMutation = useMutation({
    mutationFn: (data) => followUpsAPI.create(data),
    onSuccess: () => {
      invalidateAfterQuickAction();
      toast.success("Follow-up scheduled successfully");
      closeQuickAction();
    },
    onError: () => toast.error("Failed to schedule follow-up"),
  });

  const quickSendEmailMutation = useMutation({
    mutationFn: ({ templateId, data }) => emailTemplatesAPI.send(templateId, data),
    onSuccess: (response) => {
      toast.success(response.data?.message || "Email sent successfully");
      closeQuickAction();
    },
    onError: (error) =>
      toast.error(
        error.response?.data?.message || error.message || "Failed to send email",
      ),
  });

  const quickSendCustomEmailMutation = useMutation({
    mutationFn: (data) => emailTemplatesAPI.sendCustom(data),
    onSuccess: (response) => {
      toast.success(response.data?.message || "Email sent successfully");
      closeQuickAction();
    },
    onError: (error) =>
      toast.error(
        error.response?.data?.message || error.message || "Failed to send email",
      ),
  });

  // Initialize form data when lead loads
  React.useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name || "",
        phone: lead.phone || "",
        alternatePhone: lead.alternatePhone || "",
        email: lead.email || "",
        stage: lead.stage || "new",
        status: lead.status || "new",
        source: lead.source || "",
        notes: typeof lead.notes === "string" ? lead.notes : "",
        companyName: lead.companyName || "",
        designation: lead.designation || "",
        industry: lead.industry || "",
        website: lead.website || "",
        gstNumber: lead.gstNumber || "",
        companySize: lead.companySize || "",
        annualRevenue: lead.annualRevenue || "",
        tagsString: Array.isArray(lead.tags) ? lead.tags.join(", ") : "",
        addressStreet: lead.address?.street || "",
        addressCity: lead.address?.city || "",
        addressState: lead.address?.state || "",
        addressPincode: lead.address?.pincode || "",
        addressCountry: lead.address?.country || "",
      });
    }
  }, [lead]);

  const handleSave = () => {
    // Validate form
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    } else if (/\d/.test(formData.name)) {
      errors.name = "Name cannot contain numbers";
    } else if (formData.name.trim().length > 100) {
      errors.name = "Name must be 100 characters or less";
    }

    const phoneValidation = validatePhoneNumber(formData.phone);
    if (!phoneValidation.isValid) {
      errors.phone = phoneValidation.error;
    }

    const altPhoneValidation = validateOptionalPhone(formData.alternatePhone);
    if (!altPhoneValidation.isValid) {
      errors.alternatePhone = altPhoneValidation.error;
    }

    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      errors.email = emailValidation.error;
    }

    const pincodeValidation = validatePincode(formData.addressPincode);
    if (!pincodeValidation.isValid) {
      errors.addressPincode = pincodeValidation.error;
    }

    const gstValidation = validateGstNumber(formData.gstNumber);
    if (!gstValidation.isValid) {
      errors.gstNumber = gstValidation.error;
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    // Assemble nested address + tags array from the flat form fields
    const payload = {
      name: formData.name.trim(),
      phone: phoneValidation.isValid ? phoneValidation.cleanPhone : formData.phone.trim(),
      alternatePhone: altPhoneValidation.isValid ? altPhoneValidation.cleanPhone : formData.alternatePhone.trim(),
      email: formData.email.trim(),
      stage: formData.stage,
      status: formData.status,
      source: formData.source.trim(),
      notes: formData.notes,
      companyName: formData.companyName.trim(),
      designation: formData.designation.trim(),
      industry: formData.industry.trim(),
      website: formData.website.trim(),
      gstNumber: gstValidation.isValid ? gstValidation.cleanGst : formData.gstNumber.trim(),
      companySize: formData.companySize.trim(),
      annualRevenue: formData.annualRevenue.trim(),
      tags: (formData.tagsString || "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      address: {
        street: formData.addressStreet.trim(),
        city: formData.addressCity.trim(),
        state: formData.addressState.trim(),
        pincode: pincodeValidation.isValid ? pincodeValidation.cleanPincode : formData.addressPincode.trim(),
        country: formData.addressCountry.trim(),
      },
    };
    updateMutation.mutate(payload);
  };

  const handleNameChange = (e) => {
    let value = e.target.value;
    // Prevent numbers in name
    if (/\d/.test(value)) return;
    // Limit to 100 characters
    if (value.length > 100) value = value.slice(0, 100);
    setFormData({ ...formData, name: value });
    if (formErrors.name) setFormErrors({ ...formErrors, name: "" });
  };

  const handlePhoneChange = (e) => {
    const value = sanitizePhoneInput(e.target.value);
    setFormData({ ...formData, phone: value });
    if (formErrors.phone) setFormErrors({ ...formErrors, phone: "" });
  };

  const handleAlternatePhoneChange = (e) => {
    const value = sanitizePhoneInput(e.target.value);
    setFormData({ ...formData, alternatePhone: value });
    if (formErrors.alternatePhone) setFormErrors({ ...formErrors, alternatePhone: "" });
  };

  const handlePincodeChange = (e) => {
    const value = sanitizePincodeInput(e.target.value, 6);
    setFormData({ ...formData, addressPincode: value });
    if (formErrors.addressPincode) setFormErrors({ ...formErrors, addressPincode: "" });
  };

  const handleGstChange = (e) => {
    const value = sanitizeGstInput(e.target.value);
    setFormData({ ...formData, gstNumber: value });
    if (formErrors.gstNumber) setFormErrors({ ...formErrors, gstNumber: "" });
  };

  const handleEmailChange = (e) => {
    setFormData({ ...formData, email: e.target.value });
    if (formErrors.email) setFormErrors({ ...formErrors, email: "" });
  };

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  if (isLoading) {
    return (
      <PageLayout>
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </PageLayout>
    );
  }

  if (!lead) {
    const isInvalidLink = !isValidLeadId || leadErrorStatus === 400;
    const isNotFound = isInvalidLink || !leadError || leadErrorStatus === 404 || leadErrorStatus === 403;
    return (
      <PageLayout>
        <div className="p-4 lg:p-6">
          <div className="max-w-md mx-auto mt-10 bg-white rounded-2xl border border-gray-200 shadow-sm p-6 text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-red-50 flex items-center justify-center">
              <HiOutlineExclamationCircle className="w-7 h-7 text-red-500" />
            </div>
            <h2 className="mt-4 text-lg font-bold text-gray-900">
              {isInvalidLink ? "Invalid lead link" : isNotFound ? "Lead not found" : "Couldn't load this lead"}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {isInvalidLink
                ? "This link doesn't point to a valid lead. Check the URL or open the lead from the Leads list."
                : isNotFound
                  ? "This lead may have been deleted, or you don't have access to it."
                  : "Something went wrong while loading the lead. Please try again."}
            </p>
            <div className="mt-5 flex items-center justify-center gap-2">
              {!isNotFound && (
                <Button variant="outline" onClick={() => refetchLead()}>
                  Try again
                </Button>
              )}
              <Button
                variant="primary"
                icon={<HiOutlineArrowLeft className="w-4 h-4" />}
                onClick={() => navigate("/app/leads")}
              >
                Back to Leads
              </Button>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="p-4 lg:p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              icon={<HiOutlineArrowLeft className="w-5 h-5" />}
              onClick={() => navigate("/app/leads")}
            />
            {/* Prev / Next lead navigation — steps through the leads list order */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={() => goToLead(prevLead)}
                disabled={!prevLead}
                title={prevLead ? `Previous: ${prevLead.name}` : "No previous lead"}
                className="p-1.5 rounded-lg text-gray-500 hover:text-primary-600 hover:bg-primary-50 disabled:opacity-30 disabled:hover:text-gray-500 disabled:hover:bg-transparent dark:text-gray-400 dark:hover:text-primary-300 dark:hover:bg-primary-500/10 transition-colors"
              >
                <HiOutlineChevronLeft className="w-5 h-5" />
              </button>
              {navIndex >= 0 && navLeads?.length > 0 && (
                <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums min-w-[3.5rem] text-center">
                  {navIndex + 1} / {navLeads.length}
                </span>
              )}
              <button
                type="button"
                onClick={() => goToLead(nextLead)}
                disabled={!nextLead}
                title={nextLead ? `Next: ${nextLead.name}` : "No next lead"}
                className="p-1.5 rounded-lg text-gray-500 hover:text-primary-600 hover:bg-primary-50 disabled:opacity-30 disabled:hover:text-gray-500 disabled:hover:bg-transparent dark:text-gray-400 dark:hover:text-primary-300 dark:hover:bg-primary-500/10 transition-colors"
              >
                <HiOutlineChevronRight className="w-5 h-5" />
              </button>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{lead.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded text-sm font-medium capitalize"
                  style={{
                    backgroundColor: stageMap[lead.stage]?.color ? `${stageMap[lead.stage].color}20` : '#6B728020',
                    color: stageMap[lead.stage]?.color || '#6B7280',
                  }}
                >
                  {stageMap[lead.stage]?.name || lead.stage?.replace(/_/g, ' ') || 'Unknown'}
                </span>
                <span className="text-sm text-gray-500">
                  Created{" "}
                  {lead.createdAt
                    ? format(new Date(lead.createdAt), "MMM d, yyyy")
                    : "-"}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  icon={<HiOutlineSave className="w-4 h-4" />}
                  onClick={handleSave}
                  loading={updateMutation.isPending}
                >
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  icon={<HiOutlinePencil className="w-4 h-4" />}
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  icon={<HiOutlineTrash className="w-4 h-4" />}
                  onClick={() => setShowDeleteModal(true)}
                >
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
          {/* Lead Info Card */}
          <Card className="lg:col-span-2" padding="p-4 sm:p-6">
            <h2 className="font-semibold text-gray-900 mb-4">
              Lead Information
            </h2>

            {isEditing ? (
              <div className="space-y-5">
                {/* Contact */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-3">
                    Contact
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Input
                        label="Name"
                        value={formData.name}
                        onChange={handleNameChange}
                        error={formErrors.name}
                        maxLength={100}
                      />
                      <p className="text-xs text-right mt-0.5 text-secondary-400">
                        {formData.name.length}/100
                      </p>
                    </div>
                    <Input
                      label="Phone"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      error={formErrors.phone}
                    />
                    <Input
                      label="Alternate Phone"
                      value={formData.alternatePhone || ""}
                      onChange={handleAlternatePhoneChange}
                      error={formErrors.alternatePhone}
                    />
                    <Input
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={handleEmailChange}
                      error={formErrors.email}
                    />
                    <Select
                      label="Stage"
                      options={stageOptions}
                      value={formData.stage}
                      onChange={(e) =>
                        setFormData({ ...formData, stage: e.target.value })
                      }
                    />
                    <Select
                      label="Status"
                      options={statusOptions}
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({ ...formData, status: e.target.value })
                      }
                    />
                    <Input
                      label="Source"
                      value={formData.source || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, source: e.target.value })
                      }
                      placeholder="e.g. website, referral, campaign"
                    />
                  </div>
                </div>

                {/* Company */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-3">
                    Company
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Company Name"
                      value={formData.companyName || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, companyName: e.target.value })
                      }
                    />
                    <Input
                      label="Designation"
                      value={formData.designation || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, designation: e.target.value })
                      }
                    />
                    <Input
                      label="Industry"
                      value={formData.industry || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, industry: e.target.value })
                      }
                    />
                    <Input
                      label="Website"
                      value={formData.website || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, website: e.target.value })
                      }
                      placeholder="https://..."
                    />
                    <Input
                      label="GST Number"
                      value={formData.gstNumber || ""}
                      onChange={handleGstChange}
                      error={formErrors.gstNumber}
                    />
                    <Input
                      label="Company Size"
                      value={formData.companySize || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, companySize: e.target.value })
                      }
                      placeholder="e.g. 11-50"
                    />
                    <Input
                      label="Annual Revenue"
                      value={formData.annualRevenue || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, annualRevenue: e.target.value })
                      }
                    />
                    <Input
                      label="Tags (comma separated)"
                      value={formData.tagsString || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, tagsString: e.target.value })
                      }
                      placeholder="e.g. VIP, Wholesale"
                    />
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-3">
                    Address
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Input
                        label="Street"
                        value={formData.addressStreet || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, addressStreet: e.target.value })
                        }
                        placeholder="Street / building / area"
                      />
                    </div>
                    <Input
                      label="City"
                      value={formData.addressCity || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, addressCity: e.target.value })
                      }
                    />
                    <Input
                      label="State"
                      value={formData.addressState || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, addressState: e.target.value })
                      }
                    />
                    <Input
                      label="Pincode"
                      value={formData.addressPincode || ""}
                      onChange={handlePincodeChange}
                      error={formErrors.addressPincode}
                    />
                    <Input
                      label="Country"
                      value={formData.addressCountry || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, addressCountry: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Notes
                  </label>
                  <textarea
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none"
                    rows={4}
                    value={formData.notes || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    placeholder="Add notes..."
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                {/* Important Details — highlighted panel: name, phone, email + notes */}
                <div className="rounded-2xl border border-primary-100 bg-gradient-to-br from-primary-50/70 via-white to-white p-4 sm:p-5 dark:border-primary-500/20 dark:from-primary-500/10 dark:via-[#161c36] dark:to-[#161c36]">
                  <div className="flex items-center gap-2 mb-3">
                    <HiOutlineStar className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300">
                      Important Details
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {/* Name */}
                    <div className="bg-white rounded-xl border border-gray-100 p-3.5 shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-500/15 flex items-center justify-center flex-shrink-0">
                          <HiOutlineUser className="w-4 h-4 text-indigo-600 dark:text-indigo-300" />
                        </span>
                        <p className="text-xs text-gray-500">Name</p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900 break-words pl-10 -mt-1">
                        {lead.name || "-"}
                      </p>
                    </div>
                    {/* Phone */}
                    <div className="bg-white rounded-xl border border-gray-100 p-3.5 shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-500/15 flex items-center justify-center flex-shrink-0">
                          <HiOutlinePhone className="w-4 h-4 text-green-600 dark:text-green-400" />
                        </span>
                        <p className="text-xs text-gray-500">Phone</p>
                      </div>
                      <a
                        href={`tel:${lead.phone || ""}`}
                        className="text-sm font-semibold text-gray-900 hover:text-primary-600 dark:hover:text-primary-400 break-words pl-10 -mt-1"
                      >
                        {lead.phone || "-"}
                      </a>
                    </div>
                    {/* Email */}
                    <div className="bg-white rounded-xl border border-gray-100 p-3.5 shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                          <HiOutlineMail className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                        </span>
                        <p className="text-xs text-gray-500">Email</p>
                      </div>
                      <a
                        href={lead.email ? `mailto:${lead.email}` : undefined}
                        className="text-sm font-semibold text-gray-900 hover:text-primary-600 dark:hover:text-primary-400 break-words block pl-10 -mt-1"
                      >
                        {lead.email || "-"}
                      </a>
                    </div>
                  </div>

                  {/* Notes — kept with the important data */}
                  {lead.notes && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500 mb-2 flex items-center gap-1.5">
                        <HiOutlineChatAlt className="w-3.5 h-3.5" />
                        Notes
                      </p>
                      <div className="space-y-2">
                        {parseNotes(lead.notes).map((entry, index) => (
                          <div
                            key={index}
                            className="bg-white border-l-4 border-primary-300 dark:border-primary-500/40 rounded-lg px-3.5 py-2.5 shadow-sm"
                          >
                            <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                              {entry.text || "—"}
                            </p>
                            {entry.at && (
                              <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                                <HiOutlineClock className="w-3.5 h-3.5 flex-shrink-0" />
                                {format(new Date(entry.at), "MMM d, yyyy · h:mm a")}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Other Details — everything non-critical */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                    Other Details
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <InfoField
                      label="Alternate Phone"
                      value={lead.alternatePhone}
                      icon={<HiOutlinePhone className="w-4 h-4" />}
                    />
                    <InfoField
                      label="Source"
                      value={lead.source ? lead.source.replace(/_/g, " ") : ""}
                    />
                    <div>
                      <p className="text-sm text-gray-500">Stage</p>
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-sm font-medium capitalize mt-1"
                        style={{
                          backgroundColor: stageMap[lead.stage]?.color ? `${stageMap[lead.stage].color}20` : '#6B728020',
                          color: stageMap[lead.stage]?.color || '#6B7280',
                        }}
                      >
                        {stageMap[lead.stage]?.name || lead.stage?.replace(/_/g, ' ') || 'Unknown'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <span className={`font-medium status-${lead.status} capitalize`}>
                        {formatLabel(lead.status)}
                      </span>
                    </div>
                    <InfoField
                      label="Assigned To"
                      value={
                        lead.assignedTo
                          ? `${lead.assignedTo.firstName} ${lead.assignedTo.lastName}`
                          : ""
                      }
                      icon={<HiOutlineUser className="w-4 h-4" />}
                    />
                    <InfoField
                      label="Created"
                      value={
                        lead.createdAt
                          ? format(new Date(lead.createdAt), "MMM d, yyyy")
                          : ""
                      }
                    />
                  </div>
                </div>

                {/* Company */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                    Company
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <InfoField
                      label="Company Name"
                      value={lead.companyName}
                      icon={<HiOutlineOfficeBuilding className="w-4 h-4" />}
                    />
                    <InfoField
                      label="Designation"
                      value={lead.designation}
                      icon={<HiOutlineIdentification className="w-4 h-4" />}
                    />
                    <InfoField
                      label="Industry"
                      value={lead.industry}
                    />
                    <InfoField
                      label="Website"
                      value={lead.website}
                      icon={<HiOutlineGlobeAlt className="w-4 h-4" />}
                    />
                    <InfoField
                      label="GST Number"
                      value={lead.gstNumber}
                    />
                    <InfoField
                      label="Company Size"
                      value={lead.companySize}
                      icon={<HiOutlineUsers className="w-4 h-4" />}
                    />
                    <InfoField
                      label="Annual Revenue"
                      value={lead.annualRevenue}
                      icon={<HiOutlineCurrencyDollar className="w-4 h-4" />}
                    />
                    <div>
                      <p className="text-sm text-gray-500">Tags</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {lead.tags && lead.tags.length > 0 ? (
                          lead.tags.map((tag, i) => (
                            <span
                              key={i}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200"
                            >
                              <HiOutlineTag className="w-3 h-3 mr-1" />
                              {tag}
                            </span>
                          ))
                        ) : (
                          <span className="font-medium text-gray-900">-</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                    Address
                  </h3>
                  <div className="flex items-start gap-2">
                    <HiOutlineLocationMarker className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                    <span className="font-medium text-gray-900 break-words">
                      {formatAddress(lead.address) || "-"}
                    </span>
                  </div>
                </div>

                {/* Lead-level attachments (from quick notes) */}
                {lead.attachments && lead.attachments.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Attachments ({lead.attachments.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {lead.attachments.map((att, aIndex) => {
                        const isImg = isImageAttachment(att);
                        const url = filePublicUrl(att.url);
                        const label = att.originalName || att.fileName || 'Attachment';
                        const cls = "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors max-w-[240px] cursor-pointer";
                        const inner = (
                          <>
                            <HiOutlinePaperClip className="w-4 h-4 flex-shrink-0" />
                            <span className="text-xs font-medium truncate">{label}</span>
                          </>
                        );
                        return isImg ? (
                          <button
                            key={att._id || `${att.fileName}-${aIndex}`}
                            type="button"
                            title={label}
                            className={cls}
                            onClick={() => setPreviewImage(url)}
                          >
                            {inner}
                          </button>
                        ) : (
                          <a
                            key={att._id || `${att.fileName}-${aIndex}`}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={label}
                            className={cls}
                          >
                            {inner}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Right column: Assignment + Recent Calls, stacked to balance the tall Lead Info card */}
          <div className="lg:col-span-1 space-y-4">
            {!isTelecaller && (
            <Card padding="p-4 sm:p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Assignment</h2>
              <div className="space-y-4">
                {lead.assignedTo ? (
                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <HiOutlineUserAdd className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Assigned to</p>
                      <p className="font-medium text-gray-900">
                        {lead.assignedTo.firstName} {lead.assignedTo.lastName}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <HiOutlineUserAdd className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <p className="font-medium text-amber-700">Unassigned</p>
                    </div>
                  </div>
                )}
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  icon={<HiOutlineUserAdd className="w-5 h-5" />}
                  onClick={() => setShowAssignModal(true)}
                >
                  {lead.assignedTo ? "Change Assignment" : "Assign Lead"}
                </Button>
              </div>
            </Card>
            )}

            {/* Quick Actions — same set as the leads list page, above the timeline */}
            <Card padding="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-100 to-primary-50 flex items-center justify-center flex-shrink-0">
                  <HiOutlineLightningBolt className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">Quick Actions</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Update this lead without leaving the page
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {QUICK_ACTIONS.map((action) => {
                  if (action.requiresEmail && !lead.email) return null;
                  if (action.requiresPhone && !lead.phone) return null;
                  if (isTelecaller && action.key === "call") return null;
                  const Icon = action.icon;
                  return (
                    <button
                      key={action.key}
                      type="button"
                      onClick={() => {
                        if (action.key === "call") {
                          window.open(`tel:${lead.phone}`, "_self");
                          return;
                        }
                        if (action.key === "visit") {
                          setShowVisitModal(true);
                          return;
                        }
                        openQuickAction(action.key);
                      }}
                      className="group flex flex-col items-center gap-1.5 px-3 py-3.5 rounded-xl bg-gray-50 border border-gray-100 text-center transition-all duration-200 hover:bg-white hover:border-primary-200 hover:shadow-md active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-primary-500/40 dark:hover:border-primary-500/40"
                    >
                      <span
                        className={`w-9 h-9 rounded-lg flex items-center justify-center ${action.tileClass} transition-transform duration-200 group-hover:scale-110`}
                      >
                        <Icon className={`w-5 h-5 ${action.iconClass}`} />
                      </span>
                      <span className="text-xs font-semibold text-gray-700 group-hover:text-gray-900 leading-tight">
                        {action.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* Activity Timeline */}
            <Card padding="p-4 sm:p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Activity Timeline</h2>
              <LeadActivityTimeline leadId={id} />
            </Card>
          </div>
        </div>
      </div>

      {/* Schedule Visit / Meeting (Quick Actions) */}
      <VisitModal
        isOpen={showVisitModal}
        lead={lead}
        onClose={() => setShowVisitModal(false)}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ["visits"] });
          queryClient.invalidateQueries({ queryKey: ["visits-calendar"] });
        }}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Lead"
        size="sm"
      >
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete <strong>{lead.name}</strong>? This
          action cannot be undone.
        </p>
        <div className="flex gap-3 justify-end">
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            loading={deleteMutation.isPending}
          >
            Delete Lead
          </Button>
        </div>
      </Modal>

      {/* Quick Action Modals (shared with the leads list page) */}
      <QuickStageModal
        isOpen={activeQuickAction === "stage"}
        onClose={closeQuickAction}
        lead={lead}
        stages={stagesData || []}
        onConfirm={({ stage }) => quickUpdateStageMutation.mutate({ id, stage })}
        isLoading={quickUpdateStageMutation.isPending}
      />
      <QuickStatusModal
        isOpen={activeQuickAction === "status"}
        onClose={closeQuickAction}
        lead={lead}
        onConfirm={({ status }) => quickUpdateStatusMutation.mutate({ id, status })}
        isLoading={quickUpdateStatusMutation.isPending}
      />
      <QuickNoteModal
        isOpen={activeQuickAction === "note"}
        onClose={closeQuickAction}
        lead={lead}
        onConfirm={({ note, file }) => quickAddNoteMutation.mutate({ id, note, file })}
        isLoading={quickAddNoteMutation.isPending}
      />
      <QuickFollowUpModal
        isOpen={activeQuickAction === "followUp"}
        onClose={closeQuickAction}
        lead={lead}
        onConfirm={(data) => quickFollowUpMutation.mutate(data)}
        isLoading={quickFollowUpMutation.isPending}
      />
      <QuickSendEmailModal
        isOpen={activeQuickAction === "email"}
        onClose={closeQuickAction}
        lead={lead}
        onSendTemplate={(templateId, data) => quickSendEmailMutation.mutate({ templateId, data })}
        onSendCustom={(data) => quickSendCustomEmailMutation.mutate(data)}
        isLoading={
          quickSendEmailMutation.isPending || quickSendCustomEmailMutation.isPending
        }
      />

      {/* Assign Modal */}
      <Modal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        title={lead?.assignedTo ? "Change Assignment" : "Assign Lead"}
        size="sm"
      >
        <div className="space-y-4">
          {lead?.assignedTo && (
            <p className="text-sm text-amber-600">
              Currently assigned to:{" "}
              <strong>
                {lead.assignedTo.firstName} {lead.assignedTo.lastName}
              </strong>
            </p>
          )}
          <Select
            label="Select Telecaller"
            options={(telecallers || []).map((u) => ({
              value: u._id,
              label: `${u.firstName} ${u.lastName}`,
            }))}
            placeholder="Choose a telecaller"
            value={lead?.assignedTo?._id || ""}
            onChange={(e) => {
              updateMutation.mutate(
                { assignedTo: e.target.value },
                {
                  onSuccess: () => {
                    setShowAssignModal(false);
                    toast.success("Lead assigned successfully");
                  },
                  onError: () => toast.error("Failed to assign lead"),
                },
              );
            }}
          />
          <div className="flex gap-3 justify-end pt-4">
            <Button variant="ghost" onClick={() => setShowAssignModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                const selectEl = document.querySelector("select");
                if (!selectEl?.value) {
                  toast.error("Select a telecaller");
                  return;
                }
                updateMutation.mutate(
                  { assignedTo: selectEl.value },
                  {
                    onSuccess: () => {
                      setShowAssignModal(false);
                      toast.success("Lead assigned successfully");
                    },
                    onError: () => toast.error("Failed to assign lead"),
                  },
                );
              }}
              loading={updateMutation.isPending}
            >
              {lead?.assignedTo ? "Reassign" : "Assign"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Recording Player Modal */}
      <Modal
        isOpen={!!recordingLog}
        onClose={closeRecording}
        title="Call Recording"
        size="md"
      >
        {recordingLog && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-xl">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">
                    {recordingLog.callType === "outgoing"
                      ? "Outgoing Call"
                      : recordingLog.callType === "incoming"
                      ? "Incoming Call"
                      : "Missed Call"}
                  </p>
                  <p className="text-sm text-gray-500">
                    {recordingLog.createdAt
                      ? format(new Date(recordingLog.createdAt), "MMM d, yyyy h:mm a")
                      : "-"}
                  </p>
                </div>
                <Badge
                  variant={recordingLog.status === "connected" ? "success" : "danger"}
                  size="sm"
                >
                  {recordingLog.status?.replace("_", " ")}
                </Badge>
              </div>
              {audioUrl ? (
                <audio controls className="w-full" src={audioUrl}>
                  Your browser does not support the audio element.
                </audio>
              ) : (
                <div className="flex items-center justify-center py-4">
                  <Spinner size="sm" />
                  <span className="ml-2 text-gray-500 text-sm">Loading recording...</span>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                icon={<HiOutlineDownload className="w-4 h-4" />}
                onClick={() => downloadRecording(recordingLog)}
              >
                Download
              </Button>
              <Button variant="ghost" size="sm" onClick={closeRecording}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Attachment image preview lightbox */}
      <ImagePreviewModal src={previewImage} onClose={() => setPreviewImage(null)} />
    </PageLayout>
  );
};

export default LeadDetails;
